/**
 * Celda del cuadro de turnos (008 · T045 · US1; contracts/ui.md §1.2).
 *
 * 44 × 44 px. Muestra el código de la plantilla ("M", "N", "M+T"), "D" (descanso) o nada (sin
 * horario) y lleva un `aria-label`/`title` completo ("Martes 2: Mañana 08:00–17:00, ajuste
 * manual"): el estado **nunca** se comunica solo por color (design-system §10).
 *
 * - Origen patrón: sin marca. Ajuste manual: punto 4 px Coral 500 **y** texto en el nombre.
 * - Integración o candado (período cerrado, fuera de la relación laboral): icono `lock` 12 px + texto.
 * - Ausencia: icono del tipo sobre la celda + texto. Feriado: texto en el nombre.
 * - Error al guardar: borde Error 500 + icono + mensaje en el nombre.
 */
import {
  CircleAlert,
  FileClock,
  FileText,
  Lock,
  Plane,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';

import type { AbsenceType, GridCell as GridCellData } from '@fermin/contracts';

import { t } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { shiftsCode, shiftsDescription, weekdayLong } from '../format';

import type { KeyboardEvent, MouseEvent, Ref } from 'react';

export const ABSENCE_ICONS: Record<AbsenceType, LucideIcon> = {
  PERMISSION: FileClock,
  VACATION: Plane,
  LICENSE: FileText,
  MEDICAL_LEAVE: Stethoscope,
};

/** Código visible de la celda: `M`, `M+T`, `D` o vacío. */
export function cellCode(cell: GridCellData | undefined): string {
  if (!cell || cell.kind === null || cell.kind === 'OFF') {
    return '';
  }
  if (cell.kind === 'REST') {
    return t('schedules.grid.cell.restCode');
  }
  return shiftsCode(cell.shifts);
}

/** Nombre accesible completo de la celda. */
export function cellLabel(
  cell: GridCellData | undefined,
  weekday: number,
  day: string,
  error?: string,
): string {
  const head = `${weekdayLong(weekday)} ${Number(day.slice(8, 10))}`;
  let content: string;
  if (!cell || cell.kind === null || cell.kind === 'OFF') {
    content = t('schedules.grid.cell.off');
  } else if (cell.kind === 'REST') {
    content = t('schedules.grid.cell.rest');
  } else {
    content = shiftsDescription(cell.shifts);
  }
  const extras: string[] = [];
  if (cell?.origin === 'GRID') {
    extras.push(t('schedules.grid.cell.manual'));
  } else if (cell?.origin === 'PATTERN') {
    extras.push(t('schedules.grid.cell.pattern'));
  } else if (cell?.origin === 'INTEGRATION') {
    extras.push(t('schedules.grid.cell.integration'));
  }
  if (cell?.holiday) {
    extras.push(t('schedules.grid.cell.holiday', { name: cell.holiday.name }));
  }
  if (cell?.absence) {
    extras.push(
      cell.absence.partial
        ? t('schedules.grid.cell.absencePartial', {
            type: t(`schedules.absenceType.${cell.absence.type}`),
          })
        : t(`schedules.absenceType.${cell.absence.type}`),
    );
  }
  if (cell?.locked) {
    extras.push(t(`schedules.grid.lock.${cell.locked}`));
  }
  if (error) {
    extras.push(t('schedules.grid.cell.error', { message: error }));
  }
  const tail = extras.length > 0 ? `, ${extras.join(', ')}` : '';
  return `${head}: ${content}${tail}`;
}

export interface GridCellProps {
  cell: GridCellData | undefined;
  workerId: string;
  day: string;
  weekday: number;
  row: number;
  col: number;
  selected: boolean;
  /** Tiene el foco "roving" (`tabIndex = 0`). */
  focused: boolean;
  /** Puede entrar en la selección (con permiso y sin candado). */
  selectable: boolean;
  error: string | undefined;
  cellRef: Ref<HTMLTableCellElement>;
  onPointerDown: (row: number, col: number, shiftKey: boolean) => void;
  onPointerEnter: (row: number, col: number) => void;
  onFocusCell: (row: number, col: number) => void;
  onKeyDown: (event: KeyboardEvent<HTMLTableCellElement>, row: number, col: number) => void;
  onOpenHistory: (workerId: string, day: string) => void;
}

export function GridCell({
  cell,
  workerId,
  day,
  weekday,
  row,
  col,
  selected,
  focused,
  selectable,
  error,
  cellRef,
  onPointerDown,
  onPointerEnter,
  onFocusCell,
  onKeyDown,
  onOpenHistory,
}: GridCellProps) {
  const code = cellCode(cell);
  const label = cellLabel(cell, weekday, day, error);
  const lockedOrIntegration = Boolean(cell?.locked) || cell?.origin === 'INTEGRATION';
  const AbsenceIcon = cell?.absence ? ABSENCE_ICONS[cell.absence.type] : null;

  function handleMouseDown(event: MouseEvent<HTMLTableCellElement>) {
    if (event.button !== 0) {
      return;
    }
    onPointerDown(row, col, event.shiftKey);
  }

  return (
    <td
      ref={cellRef}
      role="gridcell"
      data-testid="schedule-grid-cell"
      data-worker={workerId}
      data-day={day}
      data-code={code}
      data-locked={cell?.locked ?? undefined}
      data-error={error ? 'true' : undefined}
      aria-selected={selectable ? selected : undefined}
      aria-disabled={selectable ? undefined : true}
      aria-label={label}
      title={label}
      tabIndex={focused ? 0 : -1}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => onPointerEnter(row, col)}
      onFocus={() => onFocusCell(row, col)}
      onKeyDown={(event) => onKeyDown(event, row, col)}
      onContextMenu={(event) => {
        event.preventDefault();
        onOpenHistory(workerId, day);
      }}
      className={cn(
        'relative h-fm-touch w-fm-touch min-w-fm-touch select-none border border-fm-border p-0 text-center',
        'text-fm-sm font-semibold text-fm-text fm-tabular',
        'transition-colors duration-fm-fast ease-fm-standard motion-reduce:transition-none',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-fm-focus',
        selectable ? 'cursor-pointer hover:bg-fm-bg-sunken' : 'cursor-default',
        cell?.locked ? 'bg-fm-bg-sunken text-fm-text-secondary' : null,
        selected ? 'bg-fm-info-soft outline-2 -outline-offset-2 outline-fm-info' : null,
        error ? 'border-2 border-fm-danger' : null,
      )}
    >
      <span aria-hidden="true">{code}</span>
      {cell?.origin === 'GRID' ? (
        <span
          aria-hidden="true"
          data-testid="schedule-grid-manual-dot"
          className="absolute right-[3px] top-[3px] size-[4px] rounded-fm-pill bg-fm-primary"
        />
      ) : null}
      {lockedOrIntegration ? (
        <Lock
          size={12}
          aria-hidden="true"
          className="absolute bottom-[2px] right-[2px] text-fm-text-secondary"
        />
      ) : null}
      {AbsenceIcon ? (
        <AbsenceIcon
          size={12}
          aria-hidden="true"
          className="absolute left-[2px] top-[2px] text-fm-info-text"
        />
      ) : null}
      {error ? (
        <CircleAlert
          size={12}
          aria-hidden="true"
          className="absolute bottom-[2px] left-[2px] text-fm-danger"
        />
      ) : null}
    </td>
  );
}
